import { ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { track } from '../utils/analytics';

function scrollToAnchor(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
}

export default function Hero() {
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handlePrimaryCta = () => {
    track('signup_start');
    navigate('/signup?plan=starter');
  };

  return (
    <div className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-green-50/30 to-emerald-50/20">
      {/* Background Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

      {/* Modern Geometric Shapes */}
      <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-emerald-400/20 to-green-600/20 rounded-full blur-xl"></div>
      <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-green-500/30 to-emerald-400/30 rounded-lg rotate-45 blur-lg"></div>
      <div className="absolute bottom-40 left-1/4 w-16 h-16 bg-gradient-to-br from-emerald-300/40 to-green-500/40 rounded-full blur-sm"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="pt-20 pb-16 md:pt-28 md:pb-20 lg:pt-32 lg:pb-24">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-200/50 backdrop-blur-sm mb-8">
              <span className="text-sm font-medium text-emerald-700 flex items-center gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                {t('landing.hero.eyebrow')}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-slate-900 mb-6 leading-tight">
              {t('landing.hero.title')}
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-slate-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              {t('landing.hero.subtitle')}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <button
                onClick={handlePrimaryCta}
                className="group px-8 py-4 bg-gradient-to-r from-emerald-600 to-green-600 text-white rounded-xl font-semibold text-lg shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/40 transform hover:-translate-y-0.5 transition-all duration-200"
              >
                {t('landing.hero.ctaPrimary')}
                <ArrowRight className="ml-2 h-5 w-5 inline-block group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={() => scrollToAnchor('product-tour')}
                className="px-8 py-4 bg-white/80 backdrop-blur-sm text-slate-700 rounded-xl font-semibold text-lg border border-slate-200 hover:bg-white hover:border-emerald-200 hover:text-emerald-700 transform hover:-translate-y-0.5 transition-all duration-200"
              >
                {t('landing.hero.ctaSecondary')}
              </button>
              <button
                onClick={() => scrollToAnchor('contact')}
                className="px-8 py-4 bg-transparent text-slate-600 rounded-xl font-semibold text-lg border border-transparent hover:text-emerald-700 transition-all duration-200"
              >
                {t('landing.hero.ctaContact')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modern Dashboard Preview */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 relative z-10">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-600/20 to-green-500/20 rounded-3xl blur-3xl transform scale-105"></div>
          <div className="relative bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-2 md:p-4">
            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200"
              alt="Sustainalyze ESG Platform Dashboard"
              className="w-full rounded-2xl shadow-lg"
            />
            <div className="absolute inset-2 md:inset-4 rounded-2xl bg-gradient-to-tr from-emerald-500/5 to-green-500/5"></div>
          </div>
        </div>
      </div>
    </div>
  );
}
